import "./App.css";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";

import KDSScreen from "./components/KDSScreen/KDSScreen";
import SettingsPage from "./components/Settings";
import AdminDashboard from "./components/AdminDashboard";
import LoginPage from "./login/LoginPage";
import QueueScreen from "./components/QueueScreen/QueueScreen";
import MobileQueueScreen from "./components/MobileQueueScreen/MobileQueueScreen";
import ReadyScreen from "./components/ReadyScreen/ReadyScreen";
import ReadyScreenSettings from "./components/ReadyScreen/ReadyScreenSettings";
import ScreensSelection from "./components/ScreenSelection/ScreensSelection";
import MobileQueueSelection from "./components/ScreenSelection/MobileQueueSelection";

import { TenantConfigProvider } from "./contexts/TenantConfigContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import RoleRoute from "./contexts/RoleRoute";
import TenantGuard from "./components/TenantGuard";

import { getToken } from "./utils/tokenProvider";

const RedirectHome = () => {
  const { auth, hasRole } = useAuth();

  if (auth.loading) return <div>Loading…</div>;
  if (!auth.isAuthenticated) return <Navigate to="/login" replace />;

  // Note: at this point TenantGuard has already confirmed the user
  // has the tenant's Login_* role. So these are "within-tenant" roles.
  if (hasRole("superadmin")) return <Navigate to="/admin" replace />;
  if (hasRole("admin")) return <Navigate to="/admin" replace />;
  if (hasRole("server")) return <Navigate to="/mobilequeuescreens" replace />;
  if (hasRole("expo")) return <Navigate to="/screenselection" replace />;
  if (hasRole("prep")) return <Navigate to="/screenselection" replace />;
  if (hasRole("reports")) return <Navigate to="/reports" replace />;

  return <Navigate to="/screenselection" replace />;
};

function App() {
  return (
    <TenantConfigProvider>
      <AuthProvider>
        <div className="App">
          <Router>
            <Routes>
              {/* Public login route */}
              <Route path="/login" element={<LoginPage />} />

              {/* Everything below here must pass TenantGuard */}
              <Route element={<TenantGuard />}>
                {/* Root redirect after successful login + tenant check */}
                <Route path="/" element={<RedirectHome />} />

                <Route
                  path="/admin"
                  element={
                    <RoleRoute allowedRoles={["admin"]}>
                      <AdminDashboard />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/expo/:stationId"
                  element={
                    <RoleRoute allowedRoles={["expo", "admin"]}>
                      <KDSScreen fixedRole="expo" />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/prep/:stationId"
                  element={
                    <RoleRoute allowedRoles={["prep", "admin"]}>
                      <KDSScreen fixedRole="prep" />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <RoleRoute allowedRoles={["expo", "prep", "admin", "reports"]}>
                      <SettingsPage />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/queuescreen"
                  element={
                    <RoleRoute allowedRoles={["admin", "expo", "prep", "reports"]}>
                      <QueueScreen />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/mobilequeuescreen"
                  element={
                    <RoleRoute allowedRoles={["admin", "expo", "prep", "reports", "server"]}>
                      <MobileQueueScreen />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/mobilequeuescreens"
                  element={
                    <RoleRoute allowedRoles={["admin", "expo", "prep", "reports", "server"]}>
                      <MobileQueueSelection />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/readyscreen"
                  element={
                    <RoleRoute allowedRoles={["admin", "expo", "prep", "reports"]}>
                      <ReadyScreen />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/readyscreensettings"
                  element={
                    <RoleRoute allowedRoles={["admin", "expo", "prep", "reports"]}>
                      <ReadyScreenSettings />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/:sublocationId/readyscreen"
                  element={
                    <RoleRoute allowedRoles={["admin", "expo", "prep", "reports"]}>
                      <ReadyScreen />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/:sublocationId/readyscreensettings"
                  element={
                    <RoleRoute allowedRoles={["admin", "expo", "prep", "reports"]}>
                      <ReadyScreenSettings />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/:sublocationId/queuescreen"
                  element={
                    <RoleRoute allowedRoles={["admin", "expo", "prep", "reports"]}>
                      <QueueScreen />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/:sublocationId/mobilequeuescreen"
                  element={
                    <RoleRoute allowedRoles={["admin", "expo", "prep", "reports", "server"]}>
                      <MobileQueueScreen />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/reports"
                  element={
                    <RoleRoute allowedRoles={["reports", "admin"]}>
                      <AdminDashboard />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/screenselection"
                  element={
                    <RoleRoute allowedRoles={["admin", "expo", "prep", "reports"]}>
                      <ScreensSelection />
                    </RoleRoute>
                  }
                />

                {/* Fallback inside guard */}
                <Route path="*" element={<RedirectHome />} />
              </Route>
            </Routes>
          </Router>
        </div>
      </AuthProvider>
    </TenantConfigProvider>
  );
}

export default App;
